import { useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { Report } from 'features/reports/report.types';
import { PaginatedAPIResponse } from 'shared/types';

type UseInitialGeoEventReportProps = {
  geoEventId?: number;
  enabled?: boolean;
};

type UseInitialGeoEventReport = {
  initialReport: Report | null;
  isLoading: boolean;
  isError: boolean;
};

const fetchInitialGeoEventReport = async (
  geoEventId: number,
): Promise<Report | null> => {
  // `status` is deliberately left out: the backend picks the oldest report
  // regardless of moderation status, so filtering here could pick a different one.
  // Passing `limit` switches the response to its paginated envelope.
  const resp = await API.get<PaginatedAPIResponse<Report>>('reports/', {
    params: {
      geo_event_id: geoEventId,
      ordering: 'date_created',
      limit: 1,
    },
  });

  return resp.data.results[0] ?? null;
};

/**
 * Fetches the oldest report of a geo-event, which is the report a geo-event was
 * created with.
 */
export const useInitialGeoEventReport = (
  props: UseInitialGeoEventReportProps,
): UseInitialGeoEventReport => {
  const { geoEventId, enabled = true } = props;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['initialGeoEventReport', geoEventId],
    queryFn: () => fetchInitialGeoEventReport(geoEventId as number),
    enabled: enabled && Boolean(geoEventId),
  });

  return {
    initialReport: data ?? null,
    isLoading,
    isError,
  };
};
